import { formatDateLocal } from "@/shared/lib/date-time-performance";
import {
  formatDocumentMoney,
  type DocumentCurrency,
} from "@/shared/lib/performance-document-currency";
import { Badge, Button } from "@devhop/ui";
import { FilePlusIcon } from "lucide-react";
import type { InstallmentDTO } from "../../domain/contracts";

const INSTALLMENT_STATUS_LABELS: Record<InstallmentDTO["status"], string> = {
  pending: "ລໍຖ້າຊຳລະ",
  paid: "ຊຳລະແລ້ວ",
  overdue: "ເກີນກຳນົດ",
  cancelled: "ຍົກເລີກ",
};

const INSTALLMENT_STATUS_VARIANTS: Record<
  InstallmentDTO["status"],
  "default" | "secondary" | "outline" | "destructive"
> = {
  pending: "secondary",
  paid: "default",
  overdue: "destructive",
  cancelled: "outline",
};

function formatMoney(value: number, currency: DocumentCurrency) {
  return formatDocumentMoney(value, currency);
}

type DocumentInstallmentLinesTableProps = {
  installments: InstallmentDTO[];
  currency: DocumentCurrency;
  showStatus?: boolean;
  totalLabel?: string;
  /** ยอดเป้าหมายของแผน (ex-tax เมื่อมี VAT) — ใช้ตรวจ total แสดง */
  planTargetAmount?: number;
  invoiceActions?: {
    canCreate: boolean;
    creatingInstallmentId?: string | null;
    onCreate: (installment: InstallmentDTO) => void;
    onOpen?: (invoiceId: string) => void;
  };
};

export function DocumentInstallmentLinesTable({
  installments,
  currency,
  showStatus = true,
  totalLabel = "ຍອດລວມ",
  planTargetAmount,
  invoiceActions,
}: DocumentInstallmentLinesTableProps) {
  const totalAmount = installments.reduce((sum, row) => sum + row.amount, 0);
  const totalMatchesTarget =
    planTargetAmount == null ||
    Math.abs(totalAmount - planTargetAmount) <= (currency === "LAK" ? 1 : 0.01);
  const showActions = Boolean(invoiceActions);
  const columnCount =
    4 + (showStatus ? 1 : 0) + (showActions ? 1 : 0);

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                #
              </th>
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ລາຍການງວດ
              </th>
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ຄົບກຳນົດ
              </th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ຈຳນວນເງິນ
              </th>
              {showStatus ? (
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ສະຖານະ
                </th>
              ) : null}
              {showActions ? (
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  ຈັດການ
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y">
            {installments.length === 0 ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-4 py-10 text-center text-muted-foreground"
                >
                  ຍັງບໍ່ມີແຜນງວດ
                </td>
              </tr>
            ) : (
              installments.map((row, index) => (
                <tr key={row.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3 tabular-nums">{index + 1}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">
                      ງວດ {row.installmentNo}
                      {row.label ? ` — ${row.label}` : ""}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {row.dueDate ? formatDateLocal(row.dueDate) : "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                    {formatMoney(row.amount, currency)}
                  </td>
                  {showStatus ? (
                    <td className="px-4 py-3">
                      <Badge
                        variant={
                          INSTALLMENT_STATUS_VARIANTS[row.status] ?? "outline"
                        }
                      >
                        {INSTALLMENT_STATUS_LABELS[row.status] ?? row.status}
                      </Badge>
                    </td>
                  ) : null}
                  {showActions && invoiceActions ? (
                    <td className="px-4 py-3 text-right">
                      {row.invoicedInvoiceId ? (
                        invoiceActions.onOpen ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const invoiceId = row.invoicedInvoiceId;
                              if (invoiceId) invoiceActions.onOpen?.(invoiceId);
                            }}
                          >
                            ເບິ່ງ INV
                          </Button>
                        ) : (
                          <Badge variant="secondary">ສ້າງແລ້ວ</Badge>
                        )
                      ) : invoiceActions.canCreate ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={
                            invoiceActions.creatingInstallmentId === row.id
                          }
                          onClick={() => invoiceActions.onCreate(row)}
                        >
                          <FilePlusIcon className="size-3.5" />
                          {invoiceActions.creatingInstallmentId === row.id
                            ? "ກຳລັງສ້າງ..."
                            : "ສ້າງ INV"}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {installments.length > 0 ? (
        <div className="flex justify-end rounded-lg border bg-muted/20 px-4 py-3">
          <div className="text-right">
            <p className="text-muted-foreground text-xs">{totalLabel}</p>
            <p
              className={
                totalMatchesTarget
                  ? "font-semibold text-base tabular-nums"
                  : "font-semibold text-base text-destructive tabular-nums"
              }
            >
              {formatMoney(totalAmount, currency)}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
